import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { PagePagination, SortDirectionCapital } from '../../types'
import { getInitialPaginationData, getInitialTableColumns } from '../../utils'
import { setTradeAiAnalysis } from '../TradingLog/TradingLogSlice'
import { Trade } from '../TradingLog/types'

import { DailyStatsInitialState } from './types'

const initialState: DailyStatsInitialState = {
  dailyStatsData: {},
  pagination: {
    ...getInitialPaginationData<Trade>(getInitialTableColumns().tradingLog as (keyof Trade)[]),
    order: SortDirectionCapital.desc,
  },
  expandedAll: false,
  expandedDate: null,
}

export const fetchDailyStats = createAsyncThunk(
  'dailyStats/fetchDailyStats',
  async (
    pagination: Pick<
      PagePagination<Trade>,
      'page' | 'perPage' | 'columns' | 'period' | 'account' | 'order'
    >,
  ) => {
    try {
      const { DailyStatsService } = await import('./DailyStatsService')
      const response = await DailyStatsService.getTrades({ ...pagination, fromCalendar: false })
      return response.data
    } catch (error) {
      throw Error(error.message)
    }
  },
)

const DailyStatsSlice = createSlice({
  name: 'DailyStats',
  initialState,
  reducers: {
    updateColumnOrder: (state, action) => {
      state.pagination.columns = action.payload
    },
    updatePageSize: (state, action) => {
      state.pagination.perPage = action.payload
    },
    updatePage: (state, action) => {
      state.pagination.page = action.payload
    },
    expandAll: (state) => {
      state.expandedAll = true
    },
    resetExpandAll: (state) => {
      state.expandedAll = null
    },
    collapseAll: (state) => {
      state.expandedAll = false
      state.expandedDate = null
    },
    fillDailyStatsData: (state, action) => {
      state.dailyStatsData = action.payload
    },
    setExpandedDate: (state, action) => {
      state.expandedDate = action.payload
    },
    clearAllDailyStats: (state) => {
      state.dailyStatsData = initialState.dailyStatsData
      state.expandedAll = initialState.expandedAll
      state.expandedDate = initialState.expandedDate
      state.pagination = initialState.pagination
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailyStats.fulfilled, (state, action) => {
        state.dailyStatsData = action.payload.data
        state.pagination = {
          ...state.pagination,
          ...action.payload.meta,
        }
      })
      .addCase(setTradeAiAnalysis, (state, action) => {
        Object.keys(state.dailyStatsData).forEach((date) => {
          state.dailyStatsData[date].tradingLogTableData = state.dailyStatsData[
            date
          ].tradingLogTableData.map((trade) =>
            Number(trade.id) === action.payload.tradeId
              ? { ...trade, tradeAiAnalysis: action.payload.analysis }
              : trade,
          )
        })
      })
  },
})

const DailyStatsReducer = DailyStatsSlice.reducer
export const {
  updateColumnOrder,
  updatePageSize,
  updatePage,
  expandAll,
  collapseAll,
  fillDailyStatsData,
  setExpandedDate,
  resetExpandAll,
  clearAllDailyStats,
} = DailyStatsSlice.actions
export default DailyStatsReducer
